import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { clone } from "../../utils/array";
import { ElementChangedFlags, UndoOperation } from "../mdl/SceneElement";
import { quaternionToAxisAngle, rotateVector3ToRef } from "../utils/MathUtils";
import { rotate, updateFrozenWorldMatrix } from "../utils/MeshUtils";
import { AbstractCommand } from "./AbstractCommand";
export class TransformElements extends AbstractCommand {
    constructor(pApp, pElements) {
        super(pApp);
        this.mElements = clone(pElements);
        this.mRedoData = null;
        this.mRedoTransforms = null;
        //store parent world matrix and transforms for undo
        this.mUndoTransforms = new Array();
        this.mUndoData = new Array();
        this.mInvWorldMatrix = new Array();
        this.mInvWorldRotation = new Array();
        this.mApp.model.eventBeginTransformElements.trigger();
        for (const tElement of this.mElements) {
            this.mUndoTransforms.push(tElement.transform.clone());
            this.mUndoData.push(tElement.saveUndoData(UndoOperation.Transform));
            if (tElement.node.parent != null) {
                const tMat = tElement.node.parent.getWorldMatrix().clone().invert();
                this.mInvWorldMatrix.push(tMat);
                const tPos = Vector3.Zero();
                const tRot = new Quaternion();
                const tScale = new Vector3(1, 1, 1);
                tMat.decompose(tScale, tRot, tPos);
                this.mInvWorldRotation.push(tRot);
            }
            else {
                this.mInvWorldMatrix.push(null);
                this.mInvWorldRotation.push(null);
            }
        }
    }
    isAvailable() {
        return (this.mElements != null);
    }
    isUndoable() {
        return (true);
    }
    execute(pParameter) {
        //notify elements that transformation has changed
        this.mApp.model.beginModification();
        for (const tElement of this.mElements) {
            tElement.notifyChange(ElementChangedFlags.Transformation);
        }
        this.mApp.model.eventEndTransformElements.trigger();
        this.mApp.model.endModification();
        //store transforms for redo
        this.mRedoTransforms = new Array(this.mElements.length);
        this.mRedoData = new Array();
        for (let i = 0; i < this.mElements.length; ++i) {
            this.mRedoTransforms[i] = this.mElements[i].transform.clone();
            this.mRedoData[i] = this.mElements[i].saveUndoData(UndoOperation.Transform);
        }
        return (Promise.resolve(true));
    }
    undo() {
        this.mApp.model.eventBeginTransformElements.trigger();
        this.undoTransforms();
        return (Promise.resolve());
    }
    redo() {
        this.mApp.model.beginModification();
        this.mApp.model.eventBeginTransformElements.trigger();
        if (this.mRedoData != null && this.mRedoTransforms != null) {
            for (let i = 0; i < this.mElements.length; ++i) {
                this.mElements[i].setTransform(this.mRedoTransforms[i]);
            }
            for (let i = 0; i < this.mElements.length; ++i) {
                if (this.mRedoData[i] != null)
                    this.mElements[i].loadUndoData(UndoOperation.Transform, this.mRedoData[i]);
            }
        }
        this.mApp.model.eventEndTransformElements.trigger();
        this.mApp.model.endModification();
        this.mApp.viewer.requestRenderFrame();
        return (Promise.resolve());
    }
    /**
     * pTranslation is given in world coordinates.
     */
    translate(pTranslation) {
        for (let i = 0; i < this.mElements.length; ++i) {
            let tTranslation = pTranslation;
            if (this.mInvWorldMatrix[i] != null)
                tTranslation = Vector3.TransformNormal(tTranslation, this.mInvWorldMatrix[i]);
            this.mElements[i].node.position.addInPlace(tTranslation);
            updateFrozenWorldMatrix(this.mElements[i].node);
        }
        this.mApp.rendering.notifyModelInteraction();
        this.mApp.viewer.requestRenderFrame();
    }
    /**
     * pRotation and pRefPoint are given in world coordinates if pLocal = false, otherwise in
     * local coordinates.
     */
    rotate(pRotation, pLocal, pRefPoint = null) {
        for (let i = 0; i < this.mElements.length; ++i) {
            let tRefPoint = pRefPoint;
            let tRotation = pRotation;
            if (!pLocal && this.mInvWorldMatrix[i] != null) {
                const tAxisAngle = quaternionToAxisAngle(pRotation);
                const tAxis = new Vector3(tAxisAngle.x, tAxisAngle.y, tAxisAngle.z);
                rotateVector3ToRef(tAxis, this.mInvWorldRotation[i], tAxis);
                tRotation = Quaternion.RotationAxis(tAxis, tAxisAngle.w);
                if (tRefPoint != null)
                    tRefPoint = Vector3.TransformCoordinates(tRefPoint, this.mInvWorldMatrix[i]);
            }
            rotate(this.mElements[i].node, tRotation, pLocal, tRefPoint);
            updateFrozenWorldMatrix(this.mElements[i].node);
        }
        this.mApp.rendering.notifyModelInteraction();
        this.mApp.viewer.requestRenderFrame();
    }
    undoTransforms() {
        this.mApp.model.beginModification();
        for (let i = 0; i < this.mElements.length; ++i) {
            this.mElements[i].setTransform(this.mUndoTransforms[i]);
        }
        for (let i = 0; i < this.mElements.length; ++i) {
            if (this.mUndoData[i] != null)
                this.mElements[i].loadUndoData(UndoOperation.Transform, this.mUndoData[i]);
        }
        this.mApp.model.eventEndTransformElements.trigger();
        this.mApp.model.endModification();
        this.mApp.viewer.requestRenderFrame();
    }
}
